import classNames from 'classnames'
import Color from 'color'
import { FinderStageId } from 'entities-next/stages'
import React, { useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { Deal } from 'scenes/Dashboard/data/deals/types'
import { organizationToString } from 'scenes/Dashboard/data/organizations/helpers'
import { Organization, OrganizationStatus } from 'scenes/Dashboard/data/organizations/types'
import { makeStyles } from 'tss-react/mui'

import { Button, Theme } from '@mui/material'

import { deleteDeal, demoteDeal, releaseDealToInvestor } from '../duck/actions'
import DeleteDealButton from './DeleteDealButton.component'
import { MoveToButton } from './MoveToButton.component'
import { ReleaseToFounderButton } from './ReleaseToFounderButton.component'

export type DealActionsProps = {
  className?: string
  deal: Deal
  organization: Organization
}

function useDealActionsData(organization: Organization) {
  const organizationReference = useMemo(() => organizationToString(organization), [organization])
  return {
    organizationReference,
  }
}

function useDealActionsFeatures(deal: Deal, organization: Organization) {
  const isReady = deal.currentFinderStage === FinderStageId.ready
  const isReleasableToFounders = isReady && organization.status !== OrganizationStatus.roundClosed
  const canRelease = isReady && organization.status !== OrganizationStatus.roundClosed

  return {
    isReleasableToFounders,
    canRelease,
  }
}

function useDealActionsHandlers(deal: Deal) {
  const dealId = deal.id
  const dispatch = useDispatch()
  const handleDemote = useCallback(() => dispatch(demoteDeal.request(dealId)), [dispatch, dealId])
  const handleDelete = useCallback(() => dispatch(deleteDeal(deal)), [dispatch, dealId])

  return {
    handleDemote,
    handleDelete,
  }
}

export const PairingActions = React.memo(({ className, deal, organization }: DealActionsProps) => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const { organizationReference } = useDealActionsData(organization)
  const { handleDemote, handleDelete } = useDealActionsHandlers(deal)
  const { canRelease, isReleasableToFounders } = useDealActionsFeatures(deal, organization)
  const handleSendToInvestor = useCallback(() => dispatch(releaseDealToInvestor.request(deal.id)), [dispatch, deal.id])

  const DemoteButton = (
    <Button
      size="small"
      color="inherit"
      variant="outlined"
      className={classNames(classes.button, classes.demoteButton)}
      onClick={handleDemote}
    >
      Demote
    </Button>
  )

  const SendDealToInvestorButton = (
    <Button size="small" color="secondary" variant="outlined" disabled={!canRelease} onClick={handleSendToInvestor}>
      Release to Investor
    </Button>
  )
  const MoveToButtonWithProps = (
    <MoveToButton data-testid="move-to-button" className={classes.button} dealId={deal.id} />
  )

  return (
    <div className={className}>
      <div className={classes.actions}>
        <div className={classes.dealActions}>
          {DemoteButton}
          {MoveToButtonWithProps}
          <DeleteDealButton organizationReference={organizationReference} onClick={handleDelete} />
        </div>
        <div className={classes.dealActions}>
          <ReleaseToFounderButton
            className={classes.button}
            organization={organization}
            disabled={!isReleasableToFounders}
          />
          {SendDealToInvestorButton}
        </div>
      </div>
    </div>
  )
})

PairingActions.displayName = 'PairingActions'

const useStyles = makeStyles()(({ palette, spacing }: Theme) => ({
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
  dealActions: {
    display: 'flex',
  },
  button: {
    marginRight: spacing(0.5),
    marginLeft: spacing(0.5),
    flex: '0 0 auto',
  },
  createUserButton: {
    color: palette.warning.main,
    borderColor: 'currentColor',
    '&:hover': {
      backgroundColor: Color(palette.warning.main).alpha(0.08).string(),
    },
  },
  demoteButton: {
    color: palette.extra.negative,
  },
  deleteButton: {
    color: palette.extra.negative,
  },
}))
